/**
 * Stocks derivations over the `getStocks` wire types from `@jums-dashboard/shared`.
 */

import type { StockPoint, StockSeries } from '@jums-dashboard/shared';

// ─── Derivations ───────────────────────────────────────────────────────────

export type UpDown = 'up' | 'down' | 'flat';

export interface DerivedAsset {
  /** Latest price (regularMarketPrice if present, else last close). */
  current: number | null;
  prevClose: number | null;
  dayChangeAbs: number | null;
  dayChangePct: number | null;
  /** Index into `points` where the most recent session/day starts; -1 if none. */
  todayStartIndex: number;
  min: number | null;
  max: number | null;
  hasVolume: boolean;
  /** Per-point close-vs-open sign, for coloring volume bars. */
  upDown: UpDown[];
  hasData: boolean;
}

function sameUtcDay(aSeconds: number, bSeconds: number): boolean {
  const a = new Date(aSeconds * 1000);
  const b = new Date(bSeconds * 1000);
  return a.getUTCFullYear() === b.getUTCFullYear() && a.getUTCMonth() === b.getUTCMonth() && a.getUTCDate() === b.getUTCDate();
}

/**
 * First index whose point shares the same UTC calendar day as the final point.
 * Intraday sessions never cross UTC midnight, so this cleanly isolates the most
 * recent trading day (and matches crypto's UTC day). Returns -1 for no points.
 */
export function computeTodayStartIndex(points: StockPoint[]): number {
  if (points.length === 0) return -1;
  const lastT = points[points.length - 1]!.t;
  for (let i = 0; i < points.length; i++) {
    if (sameUtcDay(points[i]!.t, lastT)) return i;
  }
  return -1;
}

export function deriveAsset(series: StockSeries | undefined): DerivedAsset {
  const empty: DerivedAsset = {
    current: null,
    prevClose: series?.prevClose ?? null,
    dayChangeAbs: null,
    dayChangePct: null,
    todayStartIndex: -1,
    min: null,
    max: null,
    hasVolume: false,
    upDown: [],
    hasData: false,
  };
  if (!series || series.error || series.points.length === 0) return empty;

  const { points } = series;
  const last = points[points.length - 1]!;
  const current = series.regularMarketPrice ?? last.close;
  const prevClose = series.prevClose;
  const dayChangeAbs = current != null && prevClose != null ? current - prevClose : null;
  const dayChangePct = dayChangeAbs != null && prevClose != null && prevClose !== 0 ? (dayChangeAbs / prevClose) * 100 : null;

  let min = Infinity;
  let max = -Infinity;
  let hasVolume = false;
  const upDown: UpDown[] = [];
  for (const point of points) {
    if (point.close < min) min = point.close;
    if (point.close > max) max = point.close;
    if (point.volume != null && point.volume > 0) hasVolume = true;
    upDown.push(point.close > point.open ? 'up' : point.close < point.open ? 'down' : 'flat');
  }

  return {
    current,
    prevClose,
    dayChangeAbs,
    dayChangePct,
    todayStartIndex: computeTodayStartIndex(points),
    min: Number.isFinite(min) ? min : null,
    max: Number.isFinite(max) ? max : null,
    hasVolume,
    upDown,
    hasData: true,
  };
}

// ─── Formatting ───────────────────────────────────────────────────────────

/** Adaptive decimal precision: large numbers get fewer decimals. */
function priceDigits(abs: number): number {
  if (abs >= 1000) return 0;
  if (abs >= 100) return 1;
  return 2;
}

export function formatPrice(value: number | null, currency?: string): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const digits = priceDigits(Math.abs(value));
  const formatted = value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return currency ? `${formatted} ${currency}` : formatted;
}

export function formatDayChange(abs: number | null, pct: number | null): string {
  if (abs == null || pct == null) return '—';
  const sign = abs > 0 ? '+' : abs < 0 ? '−' : '';
  const digits = priceDigits(Math.abs(abs));
  const absText = Math.abs(abs).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const pctText = Math.abs(pct).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${sign}${absText} (${sign}${pctText}%)`;
}

export function changeSign(abs: number | null): UpDown {
  if (abs == null || abs === 0) return 'flat';
  return abs > 0 ? 'up' : 'down';
}

/** Tailwind text-color class per change direction. Shared by the tile card and detail modal. */
export const CHANGE_COLOR_CLASS: Record<UpDown, string> = {
  up: 'text-(--accent-success)',
  down: 'text-(--accent-danger)',
  flat: 'text-(--text-muted)',
};
